/**
 * FoundationsPlayer.jsx — minimal, beautiful audio player.
 *
 * Feb 16 2026 · Michael. Renders a single Foundations item at
 * `/foundations/play/:itemId` with a spacious portrait player:
 *   • Big soft breathing gold orb behind the label
 *   • Item title + supporting line
 *   • Elapsed / total time
 *   • Play/pause button with hard-preload on mount so first-play
 *     is instant (audio buffered before the user taps play)
 *   • Slim progress bar
 *   • Close ← back to the Foundations home
 *
 * Design constraints from the CARE brief:
 *   • No streak pressure. No distractions.
 *   • No autoplay — the user chooses when to press play.
 *   • Full-bleed dark navy, warm gold, soft white.
 *   • Respects prefers-reduced-motion (orb doesn't breathe).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const TOK = {
  bg: "radial-gradient(ellipse at 50% 25%, #1a2e50 0%, #0d1f3a 45%, #060e1f 80%)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
};

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function FoundationsPlayer() {
  const nav = useNavigate();
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const reduce = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Fetch manifest, find our item.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/foundations/manifest`, auth());
        if (cancelled) return;
        const it = (r.data?.items || []).find((x) => x.id === itemId);
        if (!it) {
          setError("This item is no longer available.");
          return;
        }
        if (!it.audio_url) {
          setError("This installation is not yet available.");
          return;
        }
        setItem(it);
      } catch (e) {
        if (!cancelled) setError("Could not load this installation.");
      }
    })();
    return () => { cancelled = true; };
  }, [itemId]);

  // Wire audio events.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onCanPlay = () => setReady(true);
    const onTime = () => setPos(a.currentTime || 0);
    const onLoaded = () => setDur(a.duration || 0);
    const onEnd = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [item]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      setPlaying(false);
      return;
    }
    // iOS Safari: kick a manual load() so preload="auto" wakes up on
    // user gesture, then try play() a few times before giving up.
    try { a.load(); } catch (_) {}
    const tryPlay = async (attempts = 4) => {
      for (let i = 0; i < attempts; i++) {
        try {
          await a.play();
          setPlaying(true);
          return;
        } catch (_) {
          await new Promise((r) => setTimeout(r, 250));
        }
      }
    };
    tryPlay();
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const x = ((e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left) / rect.width;
    a.currentTime = Math.min(dur, Math.max(0, x * dur));
    setPos(a.currentTime);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: TOK.bg, color: TOK.creamDim }}>
        <div>
          <p className="italic" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.1rem" }}>
            {error}
          </p>
          <button
            onClick={() => nav("/foundations")}
            data-testid="foundations-player-back-on-error"
            className="mt-6 rounded-full px-6 py-3"
            style={{
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.gold,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.7rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
            }}
          >
            Back to Foundations
          </button>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: TOK.bg, color: TOK.creamDim }}>
        <div className="italic" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Loading…</div>
      </div>
    );
  }

  const pct = dur ? (pos / dur) * 100 : 0;

  return (
    <div
      className="min-h-screen flex flex-col items-center px-5 pt-6 pb-10"
      style={{ background: TOK.bg }}
      data-testid="foundations-player"
    >
      {/* Header row — close only */}
      <div className="w-full max-w-md flex items-center justify-between">
        <button
          onClick={() => { audioRef.current?.pause(); nav("/foundations"); }}
          data-testid="foundations-player-close"
          className="rounded-full px-4 py-2"
          style={{
            background: "transparent",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamDim,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          ← Close
        </button>
        <div
          className="uppercase"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.55rem",
            letterSpacing: "0.3em",
          }}
        >
          Soulumo Foundations
        </div>
      </div>

      {/* Breathing orb + title. Feb 22 2026 · softer edges, slower
          motion, no hard dark ring around the halo — the eye reads
          "living light", not "graphic". */}
      <div className="mt-14 flex flex-col items-center text-center">
        <div
          className="relative flex items-center justify-center"
          style={{ width: 240, height: 240 }}
          aria-hidden
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(240,199,144,0.42) 0%, rgba(224,169,122,0.16) 32%, rgba(224,169,122,0.06) 55%, rgba(224,169,122,0) 78%)",
              filter: "blur(16px)",
              transform: playing ? "scale(1.15)" : "scale(1)",
              opacity: playing ? 0.98 : 0.78,
              animation: reduce ? "none" : (playing ? "foundHaloBreath 10s ease-in-out infinite" : "foundHaloBreath 14s ease-in-out infinite"),
              transition: "transform 1200ms cubic-bezier(.4,.0,.2,1), opacity 1200ms",
            }}
          />
          <div
            className="absolute inset-0 rounded-full"
            style={{
              width: 140, height: 140, margin: "auto",
              background:
                "radial-gradient(circle, rgba(255,247,230,0.95) 0%, rgba(240,199,144,0.75) 30%, rgba(224,169,122,0.25) 55%, rgba(224,169,122,0) 82%)",
              filter: "blur(6px)",
              animation: reduce ? "none" : (playing ? "foundBreath 7s ease-in-out infinite" : "foundBreath 11s ease-in-out infinite"),
              transition: "filter 1200ms",
            }}
          />
        </div>
        <h1
          className="mt-8 italic"
          style={{
            color: TOK.cream,
            fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
            fontSize: "clamp(1.7rem, 5.4vw, 2.15rem)",
            fontWeight: 300,
            lineHeight: 1.2,
          }}
          data-testid="foundations-player-title"
        >
          {item.label.split(" · ")[0]}
        </h1>
        <p
          className="mt-3 max-w-sm"
          style={{
            color: "rgba(245,236,217,0.86)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "clamp(0.95rem, 3vw, 1.02rem)",
            lineHeight: 1.55,
          }}
        >
          {item.blurb}
        </p>
      </div>

      {/* Karaoke removed · Feb 23 2026 · Michael. "Let's take out the
          karaoke style auto cue — we want them to listen." Recipients
          now sit with the orb, the title and the blurb, and let the
          voice come to them without a moving script under it. */}

      {/* Play / pause. Feb 17 2026 · The `disabled={!ready}`
          gate was preventing iOS Safari users from ever tapping
          play — Safari holds `canplay` back until the first user
          gesture, but we need the gesture to arrive first. So the
          button is now always tappable; if the audio needs a beat
          to buffer, .play() will resolve as soon as it is able. */}
      <div className="mt-12 flex justify-center">
        <button
          onClick={toggle}
          data-testid="foundations-player-toggle"
          className="rounded-full transition-all flex items-center justify-center"
          style={{
            width: 88,
            height: 88,
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            boxShadow: "0 0 44px rgba(224,169,122,0.5), 0 8px 24px rgba(0,0,0,0.35)",
            cursor: "pointer",
          }}
        >
          {playing ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
          ) : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden style={{marginLeft: 4}}><path d="M7 5v14l12-7z"/></svg>
          )}
        </button>
      </div>

      {/* Progress + time */}
      <div className="mt-8 w-full max-w-md">
        <div
          onClick={seek}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={dur || 100}
          aria-valuenow={pos}
          data-testid="foundations-player-scrubber"
          className="rounded-full cursor-pointer"
          style={{ height: 4, background: "rgba(245,236,217,0.1)" }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: "linear-gradient(90deg, #f0c790 0%, #e0a97a 100%)",
              boxShadow: "0 0 8px rgba(224,169,122,0.55)",
              borderRadius: 999,
              transition: "width 200ms linear",
            }}
          />
        </div>
        <div
          className="mt-3 flex justify-between"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.22em",
          }}
        >
          <span data-testid="foundations-player-pos">{fmt(pos)}</span>
          <span data-testid="foundations-player-dur">{fmt(dur)}</span>
        </div>
      </div>

      {/* Audio element — preload=auto so the file starts buffering
          immediately on mount. First-play is effectively instant even
          without CDN caching. */}
      <audio
        ref={audioRef}
        src={`${API}${item.audio_url}`}
        preload="auto"
        playsInline
        data-testid="foundations-player-audio"
      />

      {/* Breathing keyframes — used when reduce-motion is off. The
          player orb has two layers (inner glow + outer halo) so we
          define keyframes for each. */}
      <style>{`
        @keyframes foundBreath {
          0%   { transform: scale(1); opacity: 0.86; }
          50%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 0.86; }
        }
        @keyframes foundHaloBreath {
          0%   { filter: blur(10px) brightness(0.95); }
          50%  { filter: blur(14px) brightness(1.08); }
          100% { filter: blur(10px) brightness(0.95); }
        }
        @keyframes foundLineIn {
          0%   { opacity: 0; transform: translateY(6px); }
          20%  { opacity: 1; transform: translateY(0); }
          80%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0.55; transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}


/* Karaoke strip removed Feb 23 2026 — see comment in the render tree
 * above. Kept the surrounding file structure intact so the diff is
 * minimal. */

